export type PortalShop = {
  id: string;
  name: string;
  slug: string;
  providerId: string;
  shopifyUrl: string;
  isNewCustomerPortalEnabled?: boolean | null;
  storefrontUrl?: string | null;
  hasExchanges?: boolean | null;
  hasExchangeCredit?: boolean | null;
  hasGiftCode?: boolean | null;
  hasCustomColors?: boolean | null;
  hasCustomCss?: boolean | null;
  isActive?: boolean | null;
  returnRequestsLast30Days?: number | null;
  /**
   * Date of the shop's last return request, from `statisticsShop` — so it is as
   * fresh as the last stats computation, not live. Distinct from
   * `shop.lastActivityAt`, the field identify-inactive-shops-job decides on,
   * which also moves on orders and refunds and is not exposed here.
   */
  lastActivityAt?: number | null;
};

export type TriState = "all" | "on" | "off";

export type PortalFilters = {
  search: string;
  portal: TriState;
  exchanges: TriState;
  exchangeCredit: TriState;
  giftCode: TriState;
  customColors: TriState;
  customCss: TriState;
  active: TriState;
  hasRecentReturns: TriState;
  maxReturnsLast30Days: number | null;
};

export const INITIAL_PORTAL_FILTERS: PortalFilters = {
  search: "",
  portal: "all",
  exchanges: "all",
  exchangeCredit: "all",
  giftCode: "all",
  customColors: "all",
  customCss: "all",
  active: "on",
  hasRecentReturns: "all",
  maxReturnsLast30Days: null,
};

export const DEFAULT_MAX_RETURNS_LAST_30_DAYS = 500;

export function nextTriState(state: TriState): TriState {
  if (state === "all") return "on";
  if (state === "on") return "off";
  return "all";
}

function matchesTriState(state: TriState, value: boolean): boolean {
  return state === "all" || (state === "on") === value;
}

function matchesSearch(shop: PortalShop, search: string): boolean {
  const q = search.trim().toLowerCase();

  return (
    !q ||
    shop.name.toLowerCase().includes(q) ||
    shop.slug.toLowerCase().includes(q)
  );
}

function matchesMaxReturns(shop: PortalShop, max: number | null): boolean {
  return max === null || (shop.returnRequestsLast30Days ?? 0) <= max;
}

export function filterPortalShops(
  shops: PortalShop[],
  filters: PortalFilters,
): PortalShop[] {
  return shops.filter(
    (shop) =>
      matchesSearch(shop, filters.search) &&
      matchesTriState(
        filters.portal,
        shop.isNewCustomerPortalEnabled ?? false,
      ) &&
      matchesTriState(filters.exchanges, shop.hasExchanges ?? false) &&
      matchesTriState(
        filters.exchangeCredit,
        shop.hasExchangeCredit ?? false,
      ) &&
      matchesTriState(filters.giftCode, shop.hasGiftCode ?? false) &&
      matchesTriState(filters.customColors, shop.hasCustomColors ?? false) &&
      matchesTriState(filters.customCss, shop.hasCustomCss ?? false) &&
      matchesTriState(filters.active, shop.isActive ?? true) &&
      matchesTriState(
        filters.hasRecentReturns,
        (shop.returnRequestsLast30Days ?? 0) > 0,
      ) &&
      matchesMaxReturns(shop, filters.maxReturnsLast30Days),
  );
}
